import React, { useCallback } from "react";
import { Box, Grid } from "@material-ui/core";
import Button from "components/form/flat-button";
import { EXCEL_FORMAT_TYPE, UploaderWithTips } from "components/upload/uploader-with-tips-view";
import { MAX_FILE_SIZE_TO_IMPORT_UPCS } from "./constants";
import { DATATEST } from "constants/integration";
import { formatMessage } from "lib/globalize-util";
import { useUPCUploader } from "./context-api/hooks";
import { ErrorWhileUploadingFile } from "./error-messages-while-uploading";
import { useFileSpecificationsAndTipsForUpload } from "./file-specifications-and-tips-for-upload";
import { ImportUpcStatus } from "./types";

export const UploaderUPC = (): JSX.Element => {
    const {
        handleDropFile,
        handleImport,
        importUpcStatus,
        errorMessages,
        hasDataToChange,
        isUploading,
        wasProcessed,
        resetStates,
        showTitleError,
    } = useUPCUploader();
    const messageTips = useFileSpecificationsAndTipsForUpload();
    const hasErrors = Object.keys(errorMessages ?? {}).length > 0;

    const getErrorMessage = useCallback((): React.ReactNode => {
        if (hasErrors) {
            return <ErrorWhileUploadingFile errorMessages={errorMessages} showTitle={showTitleError} />;
        }
        return null;
    }, [errorMessages, hasErrors, showTitleError]);

    if (importUpcStatus === ImportUpcStatus.FINISHED) {
        return (
            <Box paddingY={2}>
                <p data-test={DATATEST.ASSORTMENTS.UPC.IMPORT_UPC_SUCCESS_TEXT}>
                    {formatMessage("Success! Your UPCs have been uploaded.")}
                </p>
            </Box>
        );
    }

    const importUpcsStarted = importUpcStatus === ImportUpcStatus.STARTED;

    return (
        <Grid container spacing={4}>
            <Grid item xs={12}>
                <Box maxWidth="74.9rem" marginX="auto" paddingY={4}>
                    <UploaderWithTips
                        title={formatMessage("Upload a file to add or update UPC data.")}
                        maxFileSize={MAX_FILE_SIZE_TO_IMPORT_UPCS}
                        formatType={EXCEL_FORMAT_TYPE}
                        onDrop={handleDropFile}
                        tips={messageTips}
                        hasError={hasErrors}
                        errorMessage={getErrorMessage()}
                        isUploading={isUploading || importUpcsStarted}
                        wasProcessed={wasProcessed}
                        hasDataToChange={hasDataToChange}
                        callbackCloseAlert={resetStates}
                    />
                </Box>
            </Grid>

            <Grid item xs={12}>
                <Box width="100%" display="flex" flexDirection="row-reverse">
                    <Button
                        key="next"
                        data-test={DATATEST.ASSORTMENTS.UPC.IMPORT_UPC_BTN}
                        disabled={!hasDataToChange || importUpcsStarted}
                        label={importUpcsStarted ? formatMessage("Importing") : formatMessage("Import")}
                        primary={true}
                        onClick={handleImport}
                    />
                </Box>
            </Grid>
        </Grid>
    );
};
